import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Btn } from '@/design-system';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { useToast } from '@/design-system/components/toast-context';
import { useSessionStore } from '@/stores/session-store';
import { useAuthStore } from '@/stores/auth-store';

// BK-06 : modale de reconnexion in-place. Montée à la racine (main.tsx), elle
// s'ouvre quand l'api-client rencontre un 401 avec un brouillon actif — la
// session est rétablie SANS navigation, donc sans perdre le brouillon.
export function ReauthModal() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const reauthRequired = useSessionStore((s) => s.reauthRequired);
  const setReauthRequired = useSessionStore((s) => s.setReauthRequired);
  const currentUser = useAuthStore((s) => s.currentUser);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (reauthRequired) {
      setEmail(currentUser?.email ?? '');
      setPassword('');
      setError('');
    }
  }, [reauthRequired, currentUser]);

  async function handleSubmit() {
    if (loading) return;
    setError('');
    if (!email || !password) { setError(t('auth.missingFields')); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        setError(res.status === 401 ? t('auth.invalidCredentials')
          : res.status === 429 ? t('auth.tooManyAttempts')
            : t('auth.genericError'));
        return;
      }
      await useAuthStore.getState().fetchCurrentUser();
      setReauthRequired(false);
      toast({ message: t('auth.reauthSuccess'), level: 'success' });
    } catch {
      setError(t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  }

  if (!reauthRequired) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 12px', fontFamily: sans, fontSize: 13,
    color: P.t1, background: P.s2, border: `1px solid ${P.border}`,
    borderRadius: 6, outline: 'none',
  };

  return (
    <Modal
      open
      onClose={() => setReauthRequired(false)}
      title={t('auth.reauthTitle')}
      actions={
        <Btn variant="primary" label={t('auth.reauthSubmit')} loading={loading} onClick={handleSubmit} flex />
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ fontFamily: sans, fontSize: 12, color: P.t2, margin: 0, lineHeight: 1.5 }}>
          {t('auth.reauthBody')}
        </p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.emailPlaceholder')}
          style={inputStyle}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('auth.passwordPlaceholder')}
          style={inputStyle}
          autoFocus
        />
        {error && (
          <div style={{
            padding: '8px 10px', borderRadius: 6,
            background: 'hsla(340,75%,58%,0.1)', border: '1px solid hsla(340,75%,58%,0.25)',
            fontFamily: sans, fontSize: 11, color: P.neg,
          }}>
            {error}
          </div>
        )}
        {/* Entrée soumet (A2) ; le bouton visible est dans les actions de la modale. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
      </form>
    </Modal>
  );
}
